import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from '../../../utils/axios';
import { toggleWorkingOn } from '../../../utils/urls';
import { setAppWorkingOn } from '../../../utils/workingOnSlice';

export function formatWorkingSince(isoString) {
    if (!isoString) return '';
    const date = new Date(isoString);
    if (isNaN(date)) return '';
    const diff = Date.now() - date.getTime();
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (mins > 0) return `${mins}m ago`;
    return 'just now';
}

export function getInitials(name) {
    if (!name) return '?';
    return name.trim().split(/\s+/).map(s => s[0]).slice(0, 2).join('').toUpperCase();
}

// Current "working on" state for an app. The live overlay (socket / local
// toggles) wins over the payload the component was rendered from; payloads
// from before the feature existed simply have no working_by key.
export function useAppWorkingOn(app) {
    const appId = app?.form_id;
    const live = useSelector((state) => appId != null ? state.workingOn.byAppId[String(appId)] : undefined);
    const userId = useSelector((state) => state.user.userId);

    const workingBy = live ? live.working_by : (app?.working_by ?? null);
    const workingSince = live ? live.working_since : (app?.working_since ?? null);
    const isMe = !!workingBy && userId != null && String(workingBy.id) === String(userId);

    return { appId, workingBy, workingSince, isWorking: !!workingBy, isMe };
}

export function useToggleWorkingOn(app, toast) {
    const dispatch = useDispatch();
    const { appId, workingBy, workingSince, isWorking, isMe } = useAppWorkingOn(app);
    const userId = useSelector((state) => state.user.userId);
    const userName = useSelector((state) => state.user.userName);
    const firstName = useSelector((state) => state.user.firstName);
    const lastName = useSelector((state) => state.user.lastName);
    const image = useSelector((state) => state.user.image);
    const [saving, setSaving] = useState(false);

    const setWorking = useCallback(async (working) => {
        if (appId == null || saving) return;
        const previous = { app_id: appId, working_by: workingBy, working_since: workingSince };

        // Optimistic update, replaced by the server response below.
        dispatch(setAppWorkingOn({
            app_id: appId,
            working_by: working ? { id: userId, name: [firstName, lastName].filter(Boolean).join(' ') || userName, image } : null,
            working_since: working ? new Date().toISOString() : null,
        }));
        setSaving(true);
        try {
            const response = await axios.post(`${toggleWorkingOn}${appId}/`, { working });
            if (response.status !== 200) throw new Error('Failed to update working status');
            dispatch(setAppWorkingOn({
                app_id: appId,
                working_by: response.data?.working_by,
                working_since: response.data?.working_since,
            }));
        } catch (error) {
            console.error('Error toggling working on:', error);
            dispatch(setAppWorkingOn(previous));
            toast?.current?.show({ severity: 'error', summary: 'Error', detail: 'Could not update "working on this file"', life: 3000 });
        } finally {
            setSaving(false);
        }
    }, [appId, saving, workingBy, workingSince, userId, userName, firstName, lastName, image, dispatch, toast]);

    return { appId, workingBy, workingSince, isWorking, isMe, saving, setWorking };
}
